import React from "react"
import dynamic from "next/dynamic"
import { Box, Fab, Button as MUIButton } from "@mui/material"
import { makeStyles } from "makeStyles"
import Icon from "@mdi/react"
import {
	mdiTruckDelivery,
	mdiSale,
	mdiPackageVariant,
	mdiHeartOutline,
	mdiInformation,
} from "@mdi/js"

import Button from "components/Buttons/Button"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"
import Price from "components/Price"

import getDeliveryTimeWorkDaysString from "@secureo/common/utils/commerceTools/formatters/getDeliveryTimeWorkDaysString"

import useToggle from "@secureo/common/hooks/useToggle"
import { ProductDetailPageReducerState } from "@secureo/common/reducers/productDetailPageReducer"
import { flatFeesByCurrencyCode } from "@secureo/common/config/shippingZonesAndCosts"
import formatPrice from "@secureo/common/utils/forex/formatPrice"

import { manufacturerWhitelist } from "config/manufacturerWhitelist"

const ShippingCosts = dynamic(() => import("components/ShippingCosts/ShippingCosts"), {
	ssr: false,
})

const useStyles = makeStyles()({
	wishlistAndCompareButton: {
		background: "none",
		border: "1px solid #4691CE",
		boxShadow: "none",
		color: "#4691CE",
		marginLeft: 7,
		"&:hover": {
			background: "#4691CE",
			color: "white",
		},
	},
	responsiveButtonText: {
		display: "block",
	},
	priceWidth: {
		maxWidth: "100%",
		"@media only screen and (min-width: 960px)": {
			maxWidth: 450,
			border: "1px solid #B8BCBF",
			borderRadius: 5,
		},
		"@media only screen and (max-width: 960px)": {
			border: "1px solid #B8BCBF",
			borderRadius: 5,
		},
	},
	changeAmountAndWishlistSpacing: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		borderRadius: 5,
		"@media only screen and (min-width: 960px)": {
			justifyContent: "left",
		},
	},
	addToCartUI: {
		background: "whitesmoke",
		borderBottomLeftRadius: 5,
		borderBottomRightRadius: 5,
	},
})

const ProductPrice = ({ singleGrossMsrpPriceCents, singleGrossPriceCents }: ProductPriceProps) => {
	return (
		<>
			<Box style={{ color: "grey", fontSize: 14 }}>
				Statt:{" "}
				<span style={{ textDecoration: "line-through", color: "grey" }}>
					<Price priceInEuroCents={singleGrossMsrpPriceCents} />
				</span>
			</Box>
			<Box mt={-1} style={{ display: "flex", alignItems: "center" }}>
				<strong style={{ fontSize: 25 }}>
					<Price priceInEuroCents={singleGrossPriceCents} />
				</strong>{" "}
				<Box ml={1} style={{ color: "grey", fontSize: 12, marginTop: 10 }}>
					inkl. MwSt.
				</Box>
			</Box>
		</>
	)
}

interface ProductPriceProps {
	hasMainConfiguration: boolean
	singleGrossMsrpPriceCents: number
	singleGrossPriceCents: number
}

const PriceAndDelivery = ({
	addToCart,
	addToShoppingList,
	setQuantity,
	hasMainConfiguration,
	accessToken,
	reducerState,
}: Props) => {
	const { classes } = useStyles()
	const [isOpen, toggleIsOpen] = useToggle(false)

	const {
		isLoading,
		quantity,
		singleGrossPriceCents,
		singleGrossMsrpPriceCents,
		totalGrossPriceCents,
		countryCode,
		attributes: {
			shippingOriginationCountryCodesByDestinationCountryCode,
			isHaulageDelivery,
			weightKg,
			deliveryOptionsByDestinationCountryCode,
			deliveryTimeWorkDaysByShopCountryCode,
			manufacturer,
		},
	} = reducerState
	const isFreeDelivery =
		totalGrossPriceCents > flatFeesByCurrencyCode.EUR.flatFeeForSmallAmountThresholdGrossCents
	const deliveryTimeWorkDays = deliveryTimeWorkDaysByShopCountryCode?.[countryCode] ?? 0

	const isWhitelistedManufacturer = manufacturerWhitelist.includes(manufacturer)

	return (
		<>
			<Box mb={2} className={classes.priceWidth}>
				<Box
					px={2}
					pt={2}
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
					}}
				>
					<div>
						<ProductPrice
							hasMainConfiguration={hasMainConfiguration}
							singleGrossMsrpPriceCents={singleGrossMsrpPriceCents}
							singleGrossPriceCents={singleGrossPriceCents}
						/>
					</div>
				</Box>
				<Box mt={1} px={2} className={classes.changeAmountAndWishlistSpacing}>
					<div>
						<ChangeAmountButton
							quantity={quantity}
							onChange={setQuantity}
							minQuantity={1}
						/>
					</div>
					<div>
						<Fab
							color="secondary"
							aria-label="add"
							size="small"
							className={classes.wishlistAndCompareButton}
							onClick={addToShoppingList}
						>
							<Icon path={mdiHeartOutline} size={1} />
						</Fab>
					</div>
				</Box>
				<Box py={2} px={2} mt={2} className={classes.addToCartUI}>
					{isWhitelistedManufacturer ? (
						<Button
							color="primary"
							onClick={addToCart}
							disabled={isLoading}
							size="small"
							style={{ width: "100%" }}
						>
							In den Warenkorb
						</Button>
					) : (
						<Box style={{ textAlign: "center" }}>
							<p>In Kürze wieder verfügbar</p>
						</Box>
					)}
				</Box>
			</Box>
			<MUIButton
				onClick={toggleIsOpen}
				style={{
					marginTop: 10,
					textTransform: "inherit",
					fontSize: 12,
					justifyContent: "left",
					textAlign: "left",
				}}
				startIcon={<Icon path={mdiPackageVariant} size={0.8} color="#505E5F" />}
				endIcon={<Icon path={mdiInformation} size={0.7} color="#4691CE" />}
				fullWidth
			>
				{isFreeDelivery ? (
					<span>Gratis Lieferung nach DE/AT/CH</span>
				) : (
					<span>
						Gratis Lieferung ab{" "}
						{formatPrice(
							flatFeesByCurrencyCode.EUR.flatFeeForSmallAmountThresholdGrossCents,
							"de-DE",
						)}
					</span>
				)}
			</MUIButton>
			<MUIButton
				style={{
					marginTop: 2,
					textTransform: "inherit",
					fontSize: 12,
					justifyContent: "left",
					background: "none",
					cursor: "default",
				}}
				startIcon={<Icon path={mdiTruckDelivery} size={0.8} color="#505E5F" />}
				fullWidth
				disableRipple
			>
				Lieferzeit: {getDeliveryTimeWorkDaysString(deliveryTimeWorkDays)}
			</MUIButton>
			<MUIButton
				style={{
					marginTop: 2,
					textTransform: "inherit",
					fontSize: 12,
					justifyContent: "left",
					background: "none",
					cursor: "default",
				}}
				startIcon={<Icon path={mdiSale} size={0.8} color="#505E5F" />}
				fullWidth
				disableRipple
			>
				2% Vorkasse Rabatt
			</MUIButton>

			{/* Modal */}
			{isOpen && (
				<ShippingCosts
					totalGrossPriceCents={totalGrossPriceCents}
					accessToken={accessToken}
					countryCode={countryCode}
					shippingOriginationCountryCodesByDestinationCountryCode={
						shippingOriginationCountryCodesByDestinationCountryCode
					}
					isHaulageDelivery={isHaulageDelivery}
					weightKg={weightKg}
					deliveryOptionsByDestinationCountryCode={
						deliveryOptionsByDestinationCountryCode
					}
					toggleIsOpen={toggleIsOpen}
				/>
			)}
		</>
	)
}

interface Props {
	reducerState: ProductDetailPageReducerState
	setQuantity: (quantity: number) => void
	addToCart: () => void
	addToShoppingList: () => void
	hasMainConfiguration: boolean
	accessToken: string
}

export default PriceAndDelivery
